// ==========================================================
// Multi-Layer Canvas Stack & Render Pipeline Manager
// ==========================================================

import type { StageInfo } from '../stages/StageData';
import { MaskingTapeManager } from './MaskingTape';
import { PaintToolEngine, type ToolType } from './PaintBrush';
import { ViewportController } from './ViewportController';

export class CanvasManager {
  // Canvas Elements
  public bgCanvas: HTMLCanvasElement;
  public baseCanvas: HTMLCanvasElement;
  public paintCanvas: HTMLCanvasElement;
  public tapeCanvas: HTMLCanvasElement;
  public outlineCanvas: HTMLCanvasElement;
  public glossCanvas: HTMLCanvasElement;
  public cursorCanvas: HTMLCanvasElement;

  // 2D Contexts
  private bgCtx: CanvasRenderingContext2D;
  public baseCtx: CanvasRenderingContext2D;
  public paintCtx: CanvasRenderingContext2D;
  private tapeCtx: CanvasRenderingContext2D;
  private outlineCtx: CanvasRenderingContext2D;
  private glossCtx: CanvasRenderingContext2D;
  private cursorCtx: CanvasRenderingContext2D;

  // Managers
  public tapeManager: MaskingTapeManager;
  public toolEngine: PaintToolEngine;
  public viewport: ViewportController;

  // Fixed Logical Dimensions (Virtual Resolution)
  public readonly logicalWidth: number = 420;
  public readonly logicalHeight: number = 460;
  public readonly dpr: number = 2; // Fixed crisp Retina resolution

  // Undo History
  private historyStack: ImageData[] = [];
  private maxHistory: number = 20;

  constructor() {
    this.bgCanvas = document.getElementById('bg-canvas') as HTMLCanvasElement;
    this.baseCanvas = document.getElementById('object-base-canvas') as HTMLCanvasElement;
    this.paintCanvas = document.getElementById('paint-canvas') as HTMLCanvasElement;
    this.tapeCanvas = document.getElementById('tape-canvas') as HTMLCanvasElement;
    this.outlineCanvas = document.getElementById('object-outline-canvas') as HTMLCanvasElement;
    this.glossCanvas = document.getElementById('gloss-canvas') as HTMLCanvasElement;
    this.cursorCanvas = document.getElementById('cursor-canvas') as HTMLCanvasElement;

    this.bgCtx = this.bgCanvas.getContext('2d', { willReadFrequently: true })!;
    this.baseCtx = this.baseCanvas.getContext('2d', { willReadFrequently: true })!;
    this.paintCtx = this.paintCanvas.getContext('2d', { willReadFrequently: true })!;
    this.tapeCtx = this.tapeCanvas.getContext('2d')!;
    this.outlineCtx = this.outlineCanvas.getContext('2d')!;
    this.glossCtx = this.glossCanvas.getContext('2d')!;
    this.cursorCtx = this.cursorCanvas.getContext('2d')!;

    this.tapeManager = new MaskingTapeManager();
    this.toolEngine = new PaintToolEngine();
    this.viewport = new ViewportController();

    this.initFixedCanvasBuffers();
    this.initResize();
  }

  /**
   * Initialize canvas internal pixel buffer once and only once
   * This permanently preserves painting, undo history, and base object during window resizes
   */
  private initFixedCanvasBuffers() {
    const canvases = [
      { canvas: this.bgCanvas, ctx: this.bgCtx },
      { canvas: this.baseCanvas, ctx: this.baseCtx },
      { canvas: this.paintCanvas, ctx: this.paintCtx },
      { canvas: this.tapeCanvas, ctx: this.tapeCtx },
      { canvas: this.outlineCanvas, ctx: this.outlineCtx },
      { canvas: this.glossCanvas, ctx: this.glossCtx },
      { canvas: this.cursorCanvas, ctx: this.cursorCtx }
    ];

    const pixelW = this.logicalWidth * this.dpr;
    const pixelH = this.logicalHeight * this.dpr;

    canvases.forEach(({ canvas, ctx }) => {
      canvas.width = pixelW;
      canvas.height = pixelH;
      ctx.resetTransform?.();
      ctx.scale(this.dpr, this.dpr);
    });
  }

  /**
   * Responsive CSS display scaling only (Never modifies canvas.width/height)
   */
  public initResize() {
    const updateDisplaySize = () => {
      const container = document.getElementById('atelier-canvas-wrapper');
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const maxW = Math.min(rect.width - 24, 540);
      const maxH = Math.min(rect.height - 24, 600);

      const aspect = this.logicalWidth / this.logicalHeight;
      let displayW = maxW;
      let displayH = maxW / aspect;

      if (displayH > maxH) {
        displayH = maxH;
        displayW = maxH * aspect;
      }

      displayW = Math.round(displayW);
      displayH = Math.round(displayH);

      const stage = document.getElementById('canvas-stage');
      if (stage) {
        stage.style.width = `${displayW}px`;
        stage.style.height = `${displayH}px`;
      }
    };

    window.addEventListener('resize', updateDisplaySize);
    updateDisplaySize();
  }

  public loadStage(stage: StageInfo) {
    // Clear all layers
    this.clearAll();
    this.historyStack = [];

    // Render Stage Base
    this.baseCtx.clearRect(0, 0, this.logicalWidth, this.logicalHeight);
    stage.drawObject(this.baseCtx, this.logicalWidth, this.logicalHeight, 'base');

    // Render Stage Outline
    this.outlineCtx.clearRect(0, 0, this.logicalWidth, this.logicalHeight);
    stage.drawObject(this.outlineCtx, this.logicalWidth, this.logicalHeight, 'outline');

    // Save initial blank snapshot
    this.saveSnapshot();
  }

  public saveSnapshot() {
    try {
      const snap = this.paintCtx.getImageData(0, 0, this.paintCanvas.width, this.paintCanvas.height);
      this.historyStack.push(snap);
      if (this.historyStack.length > this.maxHistory) {
        this.historyStack.shift();
      }
    } catch {
      // Ignore if canvas is empty
    }
  }

  public undo(): boolean {
    if (this.historyStack.length > 1) {
      this.historyStack.pop(); // Discard current
      const prev = this.historyStack[this.historyStack.length - 1];
      this.paintCtx.putImageData(prev, 0, 0);
      return true;
    }
    return false;
  }

  public clearPaint() {
    this.paintCtx.clearRect(0, 0, this.logicalWidth, this.logicalHeight);
    this.saveSnapshot();
  }

  public clearAll() {
    const w = this.logicalWidth;
    const h = this.logicalHeight;
    this.bgCtx.clearRect(0, 0, w, h);
    this.baseCtx.clearRect(0, 0, w, h);
    this.paintCtx.clearRect(0, 0, w, h);
    this.tapeCtx.clearRect(0, 0, w, h);
    this.outlineCtx.clearRect(0, 0, w, h);
    this.glossCtx.clearRect(0, 0, w, h);
    this.cursorCtx.clearRect(0, 0, w, h);
    this.tapeManager.clearTapes();
  }

  public renderTapes() {
    this.tapeCtx.clearRect(0, 0, this.logicalWidth, this.logicalHeight);
    this.tapeManager.render(this.tapeCtx);
  }

  public renderCursor(x: number, y: number, tool: ToolType, size: number, color: string) {
    this.cursorCtx.clearRect(0, 0, this.logicalWidth, this.logicalHeight);

    this.cursorCtx.save();
    this.cursorCtx.lineWidth = 1.5;

    if (tool === 'brush') {
      this.cursorCtx.strokeStyle = color;
      this.cursorCtx.beginPath();
      this.cursorCtx.arc(x, y, size / 2, 0, Math.PI * 2);
      this.cursorCtx.stroke();

      // Center crosshair
      this.cursorCtx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
      this.cursorCtx.beginPath();
      this.cursorCtx.arc(x, y, 2, 0, Math.PI * 2);
      this.cursorCtx.stroke();

    } else if (tool === 'roller') {
      const rw = size * 2.2;
      this.cursorCtx.strokeStyle = color;
      this.cursorCtx.strokeRect(x - rw / 2, y - 6, rw, 12);

    } else if (tool === 'spray') {
      this.cursorCtx.setLineDash([3, 3]);
      this.cursorCtx.strokeStyle = color;
      this.cursorCtx.beginPath();
      this.cursorCtx.arc(x, y, size * 1.5, 0, Math.PI * 2);
      this.cursorCtx.stroke();

    } else if (tool === 'sponge') {
      this.cursorCtx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
      this.cursorCtx.beginPath();
      this.cursorCtx.arc(x, y, size * 0.75, 0, Math.PI * 2);
      this.cursorCtx.stroke();

    } else if (tool === 'peel') {
      this.cursorCtx.strokeStyle = '#ff4757';
      this.cursorCtx.setLineDash([4, 3]);
      this.cursorCtx.beginPath();
      this.cursorCtx.arc(x, y, 14, 0, Math.PI * 2);
      this.cursorCtx.stroke();

      this.cursorCtx.fillStyle = '#ff4757';
      this.cursorCtx.font = '12px sans-serif';
      this.cursorCtx.fillText('✂️', x - 6, y + 4);
    }

    this.cursorCtx.restore();
  }

  public clearCursor() {
    this.cursorCtx.clearRect(0, 0, this.logicalWidth, this.logicalHeight);
  }

  /**
   * Export fully flattened composite artwork as high-res Data URL PNG
   */
  public exportCompositePNG(): string {
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = this.paintCanvas.width;
    exportCanvas.height = this.paintCanvas.height;
    const ctx = exportCanvas.getContext('2d')!;

    // Background studio atmosphere
    const grad = ctx.createRadialGradient(
      exportCanvas.width / 2, exportCanvas.height / 2, 20,
      exportCanvas.width / 2, exportCanvas.height / 2, exportCanvas.width / 1.2
    );
    grad.addColorStop(0, '#232c3d');
    grad.addColorStop(1, '#0e1219');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

    // Layer stack
    ctx.drawImage(this.baseCanvas, 0, 0);
    ctx.drawImage(this.paintCanvas, 0, 0);
    ctx.drawImage(this.tapeCanvas, 0, 0);
    ctx.drawImage(this.outlineCanvas, 0, 0);
    ctx.drawImage(this.glossCanvas, 0, 0);

    // Craftsman signature watermark
    ctx.save();
    ctx.scale(this.dpr, this.dpr);
    ctx.font = 'bold 12px Cinzel, serif';
    ctx.fillStyle = 'rgba(229, 169, 60, 0.75)';
    ctx.fillText('PAINT MEISTER • CRAFTSMAN STUDIO', 16, this.logicalHeight - 16);
    ctx.restore();

    return exportCanvas.toDataURL('image/png');
  }

  /**
   * Convert Pointer / Touch Client coordinates to Canvas Logical coordinates
   */
  public getCanvasPoint(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.cursorCanvas.getBoundingClientRect();
    const rawX = ((clientX - rect.left) / rect.width) * this.logicalWidth;
    const rawY = ((clientY - rect.top) / rect.height) * this.logicalHeight;
    return {
      x: Math.max(0, Math.min(this.logicalWidth, rawX)),
      y: Math.max(0, Math.min(this.logicalHeight, rawY))
    };
  }
}
